import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreFromTrashOutlinedIcon from '@mui/icons-material/RestoreFromTrashOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import Header from 'shared/components/Header';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import { useAuth } from 'shared/hooks/useAuth';
import usePersistedColumnVisibility from 'shared/hooks/usePersistedColumnVisibility';
import { canViewAllRecords } from 'shared/utils/canViewAllRecords';
import { apiErrorMessage } from '../utils/responseHelpers';
import useEntityCrud from './useEntityCrud';
import CreateEntityDialog from './CreateEntityDialog';
import EditEntityDialog from './EditEntityDialog';
import DeleteEntityDialog from './DeleteEntityDialog';
import { buildGridToolbarExportProps } from 'shared/utils/tableExportFileName';
import { sortRowsNewestFirst } from 'shared/utils/sortRowsNewestFirst';

const toTableId = (entityName) =>
  `system-register.${String(entityName || 'entity')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')}`;

const isDeleted = (row, lifecycleField) =>
  String(row?.recordStatus || row?.[lifecycleField] || row?.status || '').toUpperCase() === 'DELETED';
const isSoftwareDeleted = (row) =>
  String(row?.softwareStatus || row?.status || '').toUpperCase() === 'DELETED';

const EntityCrudPage = ({ config }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = canViewAllRecords(user);
  const [showAll, setShowAll] = useState(false);
  const {
    items,
    isLoading,
    fetchItems,
    createItem,
    updateItem,
    removeItem,
    restoreItem,
    softwareOptions,
    loadSoftwareOptions,
  } = useEntityCrud(config);

  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [restoringId, setRestoringId] = useState(null);
  const tableId = useMemo(() => toTableId(config.entityName), [config.entityName]);
  const { columnVisibilityModel, onColumnVisibilityModelChange } =
    usePersistedColumnVisibility(tableId);

  const supportsSoftDeleteList = config.mode === 'lookup' || Boolean(config.supportsSoftDeleteList);
  const supportsViewAll =
    config.mode === 'lookup'
    || config.mode === 'nested'
    || config.mode === 'nestedCollection'
    || Boolean(config.supportsViewAll);
  const canShowViewAll = supportsViewAll && isAdmin;
  const canRestore = supportsSoftDeleteList && Boolean(config.api?.restore) && isAdmin;
  const lifecycleField = config.lifecycleStatusField
    || (supportsSoftDeleteList ? 'status' : 'softwareStatus');
  const tableRows = useMemo(() => sortRowsNewestFirst(items), [items]);
  const hideCreate = Boolean(config.hideCreate);
  const hideEdit = Boolean(config.hideEdit);
  const showView = Boolean(config.showView && config.getViewPath);
  const CreateDialogComponent = config.CreateDialog || CreateEntityDialog;
  const EditDialogComponent = config.EditDialog || EditEntityDialog;

  const load = useCallback(() => {
    fetchItems({ showAll: canShowViewAll ? showAll : false });
  }, [fetchItems, showAll, canShowViewAll]);

  useEffect(() => {
    if (!isAdmin && showAll) {
      setShowAll(false);
    }
  }, [isAdmin, showAll]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRestore = useCallback(
    async (row) => {
      if (!canRestore || !row?.id) return;
      setRestoringId(row.id);
      try {
        await restoreItem(row);
        toast.success(`${config.entityName} restored to active`);
        load();
      } catch (error) {
        toast.error(apiErrorMessage(error, `Failed to restore ${config.entityName.toLowerCase()}.`));
      } finally {
        setRestoringId(null);
      }
    },
    [canRestore, restoreItem, config.entityName, load]
  );

  const columns = useMemo(
    () => [
      ...config.columns,
      ...(showAll && canShowViewAll
        ? [
            {
              field: lifecycleField,
              headerName: config.lifecycleStatusField
                ? 'Record status'
                : supportsSoftDeleteList
                  ? 'Status'
                  : 'Software status',
              width: 140,
              renderCell: ({ row }) => {
                const deleted = supportsSoftDeleteList
                  ? isDeleted(row, lifecycleField)
                  : isSoftwareDeleted(row);
                const label = supportsSoftDeleteList
                  ? (isDeleted(row, lifecycleField) ? 'DELETED' : row[lifecycleField] || row.status || 'ACTIVE')
                  : (isSoftwareDeleted(row) ? 'DELETED' : row.softwareStatus || 'ACTIVE');
                return (
                  <Chip
                    size="small"
                    label={label}
                    color={deleted ? 'error' : 'success'}
                    variant="filled"
                    sx={{ fontWeight: 700, borderRadius: '999px', height: 26 }}
                  />
                );
              },
            },
          ]
        : []),
      {
        field: 'actions',
        headerName: 'Actions',
        width: canRestore || showView ? 150 : 120,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const deleted = supportsSoftDeleteList
            ? isDeleted(row, lifecycleField)
            : isSoftwareDeleted(row);
          return (
            <Box display="flex" alignItems="center" gap={0.25}>
              {showView ? (
                <Tooltip title="View details">
                  <span>
                    <IconButton
                      size="small"
                      color="info"
                      onClick={() => navigate(config.getViewPath(row))}
                      disabled={deleted && supportsSoftDeleteList}
                      aria-label={`View ${config.entityName}`}
                    >
                      <VisibilityOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
              {deleted && canRestore ? (
                <Tooltip title="Restore to active">
                  <span>
                    <IconButton
                      size="small"
                      color="success"
                      onClick={() => handleRestore(row)}
                      disabled={config.readOnlyMutations || restoringId === row.id}
                      aria-label={`Restore ${config.entityName}`}
                    >
                      <RestoreFromTrashOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : !hideEdit && (!config.canEdit || config.canEdit(row)) ? (
                <Tooltip title="Edit">
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => setEditRow(row)}
                      disabled={config.readOnlyMutations || deleted}
                      aria-label={`Edit ${config.entityName}`}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
              <Tooltip title={deleted && supportsSoftDeleteList ? 'Already deleted' : 'Mark as deleted'}>
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setDeleteTarget(row)}
                    disabled={config.readOnlyMutations || (deleted && supportsSoftDeleteList)}
                    aria-label={`Delete ${config.entityName}`}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    [
      config,
      showAll,
      canShowViewAll,
      supportsSoftDeleteList,
      canRestore,
      handleRestore,
      restoringId,
      hideEdit,
      showView,
      navigate,
      lifecycleField,
    ]
  );

  return (
    <Box m="20px">
      {!hideCreate ? (
        <CreateDialogComponent
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onSuccess={load}
          config={config}
          createItem={createItem}
          softwareOptions={softwareOptions}
        />
      ) : null}
      {!hideEdit ? (
        <EditDialogComponent
          open={Boolean(editRow)}
          onClose={() => setEditRow(null)}
          onSuccess={load}
          config={config}
          row={editRow}
          updateItem={updateItem}
          softwareOptions={softwareOptions}
        />
      ) : null}
      <DeleteEntityDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onSuccess={load}
        config={config}
        row={deleteTarget}
        removeItem={removeItem}
      />

      <Header title={config.entityName} />

      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        mt={2}
        mb={1}
        gap={1}
        flexWrap="wrap"
      >
        <Box display="flex" gap={1} flexWrap="wrap">
          {canShowViewAll ? (
            <Button
              variant={showAll ? 'contained' : 'outlined'}
              color="secondary"
              onClick={() => setShowAll((v) => !v)}
            >
              {showAll ? 'Active only' : 'View all'}
            </Button>
          ) : null}
          {!hideCreate ? (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                loadSoftwareOptions();
                setCreateOpen(true);
              }}
              disabled={config.readOnlyMutations}
            >
              Add {config.entityName}
            </Button>
          ) : null}
        </Box>
      </Box>

      <Box position="relative" sx={{ opacity: isLoading ? 0.4 : 1 }}>
        <DataGrid
          rows={tableRows}
          columns={columns}
          autoHeight
          pagination
          paginationMode="client"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25, 50]}
          disableRowSelectionOnClick
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={onColumnVisibilityModelChange}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: buildGridToolbarExportProps({
              title: config.entityName,
              tableId,
            }),
          }}
          getRowClassName={(params) =>
            (supportsSoftDeleteList
              ? isDeleted(params.row, lifecycleField)
              : isSoftwareDeleted(params.row))
              ? 'row-deleted'
              : ''
          }
          sx={{
            '& .row-deleted': {
              opacity: 0.65,
            },
          }}
        />
        {isLoading ? (
          <Box position="absolute" top="40%" left="50%" sx={{ transform: 'translate(-50%, -50%)' }}>
            <LoadingSpinner message={`Loading ${config.entityName.toLowerCase()}…`} />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default EntityCrudPage;
